import { useId, useState, type ChangeEvent } from 'react';
import {
  ApiError,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  Icon,
  errorMessage,
} from '@dataroom/shared';
import type { RoomDocument } from '@dataroom/shared';
import { useUploadReadableCopy } from '@/api/hooks';
import { contentTypeOf, fileToBase64, oversizeMessage } from '@/lib/file';

/**
 * "We couldn't read these files" (ADR-0051): every file the
 * server flagged as unreadable — a locked PDF, a damaged file, a blank scan, a
 * zip too large to unpack — with the server's plain request ("This PDF is
 * password-protected — please upload an unlocked copy.") and one button that
 * sends the readable copy as a new version of that same document. The flag
 * clears as soon as the copy arrives; the AI then reads and files it.
 */
export function UnreadableFilesCard({
  roomId,
  documents,
}: {
  roomId: string;
  documents: readonly RoomDocument[];
}) {
  const flagged = documents.filter((d) => d.unreadable);
  if (flagged.length === 0) return null;
  return (
    <Card className="border-st-check-line">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="flex items-center gap-2">
            <Icon name="alert" className="text-st-check-fg" />
            {flagged.length === 1 ? 'We couldn’t read 1 file' : `We couldn’t read ${flagged.length} files`}
          </CardTitle>
          <HelpTip id="seller.dashboard.unreadable-card" />
        </div>
        <p className="text-sm text-muted-foreground">
          Please upload a readable copy of each. It replaces the file you sent, and we file it for
          you.
        </p>
      </CardHeader>
      <CardContent>
        <ul className="divide-y rounded-md border" aria-label="Files we couldn’t read">
          {flagged.map((doc) => (
            <li key={doc.id} className="flex flex-wrap items-start justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <p className="break-words text-sm font-medium">{doc.name}</p>
                <p className="text-sm text-ink-2">{doc.unreadable?.message}</p>
              </div>
              <ReadableCopyButton roomId={roomId} document={doc} />
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/** "Upload a readable copy": picks one file and sends it as a new version of `document`. */
export function ReadableCopyButton({
  roomId,
  document,
  compact = false,
}: {
  roomId: string;
  document: Pick<RoomDocument, 'id' | 'name'>;
  /** A text link (inside a checklist row) instead of a button. */
  compact?: boolean;
}) {
  const inputId = useId();
  const upload = useUploadReadableCopy(roomId);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const tooBig = oversizeMessage(file);
    if (tooBig) {
      setMessage({ tone: 'error', text: tooBig });
      return;
    }
    setMessage(null);
    try {
      await upload.mutateAsync({
        documentId: document.id,
        body: {
          filename: file.name,
          contentType: contentTypeOf(file),
          contentBase64: await fileToBase64(file),
        },
      });
      setMessage({ tone: 'ok', text: 'Thanks — we have the new copy.' });
    } catch (err) {
      const text =
        err instanceof ApiError && err.status === 409
          ? (errorMessage(err) ?? 'This copy could not be used.')
          : (errorMessage(err) ?? 'Upload failed. Please try again.');
      setMessage({ tone: 'error', text });
    }
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <input
        id={inputId}
        type="file"
        className="sr-only"
        onChange={(e) => void onPick(e)}
        disabled={upload.isPending}
        aria-label={`Upload a readable copy of ${document.name}`}
      />
      <label
        htmlFor={inputId}
        className={
          compact
            ? 'cursor-pointer rounded text-xs font-medium text-primary hover:underline focus-within:ring-2 focus-within:ring-ring'
            : 'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-input bg-surface px-3 text-[13px] font-medium hover:bg-surface-2 focus-within:ring-2 focus-within:ring-ring'
        }
      >
        {compact ? null : <Icon name="upload" size={14} />}
        {upload.isPending ? 'Uploading…' : 'Upload a readable copy'}
      </label>
      {message ? (
        <p
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={message.tone === 'error' ? 'text-xs text-destructive' : 'text-xs text-st-certified-fg'}
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}
